import type { Ref } from 'vue';
import { ref, watch } from 'vue';

const DEFAULT_MAX_LOAD_RETRY_ATTEMPTS = 3;

export interface UseMediaRetryOptions {
  /**
   * Reactive URL to watch for changes (triggers reset after refresh)
   */
  urlRef: Ref<string | undefined>;
  /**
   * Callback to emit refresh request to parent component
   */
  onRefreshRequest: () => void;
  /**
   * Optional ref to a video element that needs to be reloaded after URL change
   */
  videoRef?: Ref<HTMLVideoElement | null>;
  /**
   * The max amount of times to retry if loading fails
   */
  maxLoadRetryAttempts?: number;
}

export interface UseMediaRetryReturn {
  isError: Ref<boolean>;
  isLoading: Ref<boolean>;
  isRefreshing: Ref<boolean>;
  /**
   * Call this when media fails to load (e.g., on @error event)
   */
  handleError: () => void;
  /**
   * Call this when media loads successfully (e.g., on @load or @loadeddata event)
   */
  handleSuccess: () => void;
}

export function useMediaRetry({
  urlRef,
  onRefreshRequest,
  videoRef,
  maxLoadRetryAttempts = DEFAULT_MAX_LOAD_RETRY_ATTEMPTS,
}: UseMediaRetryOptions): UseMediaRetryReturn {
  const isError = ref(false);
  const isLoading = ref(true);
  const retryCount = ref(0);

  // Track the URL at the time of error to detect when refresh completes
  const urlAtError = ref<string | undefined>();

  // Computed-like ref for isRefreshing (true when we're waiting for URL to change)
  const isRefreshing = ref(false);

  // Reset state when URL changes after an error triggered a refresh request
  watch(urlRef, (newUrl) => {
    if (!urlAtError.value || newUrl === urlAtError.value) {
      return;
    }

    // URL changed after error - refresh completed
    urlAtError.value = undefined;
    isRefreshing.value = false;
    isLoading.value = true;
    isError.value = false;

    if (videoRef?.value) {
      videoRef.value.load();
    }
  });

  function handleError(): void {
    if (retryCount.value >= maxLoadRetryAttempts) {
      isError.value = true;
      isLoading.value = false;
      return;
    }

    retryCount.value++;
    urlAtError.value = urlRef.value;
    isRefreshing.value = true;
    onRefreshRequest();
  }

  function handleSuccess(): void {
    isLoading.value = false;
    retryCount.value = 0;
  }

  return {
    isError,
    isLoading,
    isRefreshing,
    handleError,
    handleSuccess,
  };
}
